import React, { useState, useEffect, useMemo } from 'react'
import PrintLayout from './layouts/PrintLayout'
import { Row, Col, Card, Alert, Badge } from 'react-bootstrap'
import { css } from 'emotion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruck, faMapMarkerAlt, faPhone, faCommentDots, faPen, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import PrintVehicleSelector from './components/PrintVehicleSelector'
import DeliveryPointsDisplay from './components/DeliveryPointsDisplay'
import leMoulinApi from 'app/universal/utils/LeMoulinApi'
import { useSelector } from 'react-redux'

// Rôles autorisés à éditer le commentaire de tournée (mêmes rôles « admin » que PrivateRoute).
// Un livreur (ROLE_B2C_LIVREUR seul) n'en fait pas partie : lecture seule.
const COMMENT_EDIT_ROLES = ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_DEV', 'ROLE_ADMIN_PRO']

const STORAGE_KEYS = {
  date: 'roadmapB2C.selectedDate',
  vehicles: 'roadmapB2C.selectedVehicleIds'
}

// Une tournée sans aucune étape ce jour-là n'a rien à afficher : on la masque.
const withStops = (vehicleList = []) => (vehicleList || []).filter(vehicle => vehicle.itinerary && vehicle.itinerary.length > 0)

const buildSpotsFromVehicles = (vehicleList = []) => {
  const allSpots = []

  vehicleList.forEach(vehicle => {
    if (vehicle.itinerary && vehicle.itinerary.length > 0) {
      const spotsWithVehicle = vehicle.itinerary.map(itineraryItem => ({
        ...itineraryItem,
        vehicleName: vehicle.name,
        vehicleId: vehicle.id,
        id: itineraryItem.spot_id,
        name: itineraryItem.spot_name,
        full_address: itineraryItem.spot_address,
        full_address_auto: itineraryItem.spot_address_auto,
        number_spot: itineraryItem.spot_number,
        descr: itineraryItem.spot_description,
        lat: itineraryItem.spot_coordinates?.lat,
        lng: itineraryItem.spot_coordinates?.lng,
        delivery_time: itineraryItem.delivery_time,
        departure_time: itineraryItem.departure_time,
        order_close_time: itineraryItem.order_close_time
      }))
      allSpots.push(...spotsWithVehicle)
    }
  })

  return allSpots
}

const ItinerairesB2C = (props) => {
  // Seul un admin peut éditer le commentaire de tournée ; le livreur est en lecture seule.
  const connectedUser = useSelector((state) => state.common.user.data)
  const canEditComment = (connectedUser?.roles || []).some((role) => COMMENT_EDIT_ROLES.includes(role))
  const [selectedVehicleIds, setSelectedVehicleIds] = useState(() => {
    if (typeof window !== 'undefined') {
      try {
        const storedIds = window.localStorage.getItem(STORAGE_KEYS.vehicles)
        const parsedIds = storedIds ? JSON.parse(storedIds) : []
        return Array.isArray(parsedIds) ? parsedIds : []
      } catch (e) {
        console.error('Impossible de lire les véhicules persistés', e)
      }
    }
    return []
  })
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [deliverySpots, setDeliverySpots] = useState([])
  const [selectedDate, setSelectedDate] = useState(() => {
    return moment().format('YYYY-MM-DD')
  })
  // Édition du commentaire de tournée
  const [editingCommentVehicleId, setEditingCommentVehicleId] = useState(null)
  const [commentDraft, setCommentDraft] = useState('')
  const [savingComment, setSavingComment] = useState(false)
  const selectedVehicles = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return []
    if (!selectedVehicleIds || selectedVehicleIds.length === 0) return []
    return vehicles.filter(vehicle => selectedVehicleIds.includes(vehicle.id))
  }, [vehicles, selectedVehicleIds])

  const isToday = useMemo(() => {
    return selectedDate === moment().format('YYYY-MM-DD')
  }, [selectedDate])


  // Charger les véhicules disponibles au montage du composant
  useEffect(() => {
    loadVehicles()
  }, [selectedDate])

  // Quand les véhicules changent (suite à un changement de date),
  // réaligner la sélection existante sur les nouveaux objets et
  // reconstruire la liste des spots pour le rendu.
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedDate) {
      window.localStorage.setItem(STORAGE_KEYS.date, selectedDate)
    }
  }, [selectedDate])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEYS.vehicles, JSON.stringify(selectedVehicleIds))
    }
  }, [selectedVehicleIds])

  useEffect(() => {
    if (!selectedVehicles || selectedVehicles.length === 0) {
      setDeliverySpots([])
      return
    }
    setDeliverySpots(buildSpotsFromVehicles(selectedVehicles))
  }, [selectedVehicles])

  const loadVehicles = async () => {
    try {
      setLoading(true)
      const response = await leMoulinApi.request.roadmapB2C.getItineraries({ date: selectedDate })
      const itinerariesData = response.result
      
      // Les données sont déjà formatées par l'API
      setVehicles(withStops(itinerariesData))
    } catch (err) {
      setError('Erreur lors du chargement des itinéraires')
      console.error('Erreur chargement itinéraires:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fonction pour recharger les données et maintenir la sélection actuelle
  const refreshData = async () => {
    try {
      setLoading(true)
      const response = await leMoulinApi.request.roadmapB2C.getItineraries({ date: selectedDate })
      const itinerariesData = response.result
      
      // Les données sont déjà formatées par l'API
      setVehicles(withStops(itinerariesData))
    } catch (err) {
      setError('Erreur lors du rechargement des données')
      console.error('Erreur rechargement:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleVehicleSelection = (vehicleIds) => {
    setError(null)

    setSelectedVehicleIds(vehicleIds)
  }

  // Marque/dé-marque un point comme livré pour le jour affiché.
  // État partagé avec le BO Intendance charrette (qui affiche une coche verte sur sa carte).
  // Mise à jour optimiste du state vehicles (deliverySpots en dérive), annulée si l'API échoue.
  const toggleDelivered = async (spot) => {
    const delivered = !spot.delivered
    const previousDeliveredAt = spot.delivered_at || null
    const applyDelivered = (value, deliveredAt) => {
      setVehicles(prev => prev.map(vehicle => ({
        ...vehicle,
        itinerary: (vehicle.itinerary || []).map(point =>
          point.spot_id === spot.id ? { ...point, delivered: value, delivered_at: deliveredAt } : point
        )
      })))
    }
    // Optimiste : heure locale en attendant l'horodatage renvoyé par l'API
    applyDelivered(delivered, delivered ? moment().format('YYYY-MM-DD HH:mm:ss') : null)
    try {
      const response = await leMoulinApi.request.roadmapB2C.setSpotDelivered(spot.id, {
        date: selectedDate,
        vehicleId: spot.vehicleId,
        delivered: delivered,
        authorName: connectedUser
          ? [connectedUser.firstname, connectedUser.lastname].filter(Boolean).join(' ') || connectedUser.username || null
          : null
      })
      const deliveredAt = response?.result?.delivered_at
      if (delivered && deliveredAt) {
        applyDelivered(true, deliveredAt)
      }
    } catch (err) {
      applyDelivered(!delivered, previousDeliveredAt)
      setError('Erreur lors du marquage livré')
      console.error('Erreur marquage livré:', err)
    }
  }

  const startEditComment = (vehicle) => {
    if (!canEditComment) return
    setEditingCommentVehicleId(vehicle.id)
    setCommentDraft(vehicle.comment || '')
  }

  const cancelEditComment = () => {
    setEditingCommentVehicleId(null)
    setCommentDraft('')
  }

  const saveComment = async (vehicle) => {
    if (!canEditComment) return
    try {
      setSavingComment(true)
      setError(null)
      const trimmed = commentDraft.trim()
      await leMoulinApi.request.roadmapB2C.updateVehicleComment(vehicle.id, { comment: trimmed })
      // Mise à jour locale pour éviter un rechargement complet
      setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, comment: trimmed || null } : v))
      setEditingCommentVehicleId(null)
      setCommentDraft('')
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du commentaire')
      console.error('Erreur enregistrement commentaire:', err)
    } finally {
      setSavingComment(false)
    }
  }

  return (
    <PrintLayout title="Itinéraires de livraison B2C">
      <div className={css(styles.printStyles)}>
        <Row className={css(styles.noPrint)}>
          <Col xs={12}>
              <h4 style={{ textAlign: 'center' }}>
                <FontAwesomeIcon icon={faTruck} className={css(styles.icon)} />
                Itinéraires de livraison B2C
              </h4>
          </Col>
        </Row>

      <Row className={css(styles.noPrint)}>
        <Col xs={12} md={6}>
          <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ marginRight: 10 }}>Date de livraison</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            {isToday && (
              <Badge bg="info" className={css(styles.badge)}>
                Aujourd'hui
              </Badge>
            )}
          </div>
        </Col>
      </Row>

      {error && (
        <Row>
          <Col xs={12}>
            <Alert variant="danger" className={css(styles.alert)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col xs={12}>
          <PrintVehicleSelector
            vehicles={vehicles}
            selectedVehicles={selectedVehicles}
            onVehicleSelection={handleVehicleSelection}
            loading={loading}
          />
        </Col>
      </Row>

      {selectedVehicles.length > 0 && (
        <Row>
          <Col xs={12}>
            {selectedVehicles.map((vehicle, index) => {
              const vehicleSpots = deliverySpots.filter(spot => spot.vehicleId === vehicle.id)
              return (
                <div key={vehicle.id}>
                  <h3><FontAwesomeIcon icon={faMapMarkerAlt} className={css(styles.icon)} />Itinéraire - {vehicle.name}</h3>
                  <p>Heure de départ : {vehicle.start_at ? moment(vehicle.start_at).format('HH:mm') : 'NC'} - Heure de retour : {vehicle.end_at ? moment(vehicle.end_at).format('HH:mm') : 'NC'}
                  {vehicle.contact_phone && <br/>}
                  {vehicle.contact_phone && <>Intendance Moulin : <a href={`tel:${vehicle.contact_phone}`}><FontAwesomeIcon icon={faPhone} /> {vehicle.contact_phone}</a></>}
                  </p>

                  <div className={css(styles.commentBlock)}>
                    {canEditComment && editingCommentVehicleId === vehicle.id ? (
                      <div className={css(styles.noPrint)}>
                        <label className={css(styles.commentLabel)}>
                          <FontAwesomeIcon icon={faCommentDots} className={css(styles.icon)} />
                          Commentaire de la tournée
                        </label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={commentDraft}
                          onChange={(e) => setCommentDraft(e.target.value)}
                          placeholder="Ajouter un commentaire pour cette tournée…"
                        />
                        <div className={css(styles.commentActions)}>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={savingComment}
                            onClick={() => saveComment(vehicle)}
                          >
                            <FontAwesomeIcon icon={faCheck} /> {savingComment ? 'Enregistrement…' : 'Enregistrer'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={savingComment}
                            onClick={cancelEditComment}
                          >
                            <FontAwesomeIcon icon={faTimes} /> Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className={css(styles.commentRow)}>
                        {vehicle.comment ? (
                          <p className={css(styles.commentText)}>
                            <FontAwesomeIcon icon={faCommentDots} className={css(styles.icon)} />
                            {vehicle.comment}
                          </p>
                        ) : (
                          <span className={css(styles.commentEmpty, styles.noPrint)}>Aucun commentaire</span>
                        )}
                        {canEditComment && (
                          <button
                            type="button"
                            className={`btn btn-sm btn-outline-primary ${css(styles.noPrint)}`}
                            onClick={() => startEditComment(vehicle)}
                          >
                            <FontAwesomeIcon icon={faPen} /> {vehicle.comment ? 'Modifier' : 'Ajouter un commentaire'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <DeliveryPointsDisplay
                    selectedVehicle={vehicle}
                    deliverySpots={vehicleSpots}
                    loading={loading}
                    onRefreshData={refreshData}
                    onToggleDelivered={toggleDelivered}
                  />
                </div>
              )
            })}
          </Col>
        </Row>
      )}
      </div>
    </PrintLayout>
  )
}

const styles = {
  icon: {
    marginRight: '10px',
    color: '#007bff'
  },
  alert: {
    marginBottom: '20px',
    '@media print': {
      display: 'none'
    }
  },
  noPrint: {
    '@media print': {
      display: 'none !important'
    }
  },
  badge: {
    fontSize: '0.875rem',
    padding: '6px 12px',
    backgroundColor: '#17a2b8',
    color: '#fff',
    borderRadius: '20px',
    fontWeight: '500'
  },
  commentBlock: {
    marginBottom: '15px'
  },
  commentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },
  commentLabel: {
    display: 'block',
    fontWeight: '600',
    marginBottom: '5px'
  },
  commentText: {
    margin: 0,
    fontStyle: 'italic',
    whiteSpace: 'pre-wrap'
  },
  commentEmpty: {
    color: '#888',
    fontStyle: 'italic'
  },
  commentActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px'
  },
  printStyles: {
    '@media print': {
      '& *': {
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important'
      },
      '& .card': {
        border: 'none !important',
        boxShadow: 'none !important',
        paddingBottom: '10px !important',
        borderBottom: '1px solid #ccc !important'
      },
      '& .card-body': {
        border: 'none !important',
        padding: '0 !important'
      },
      '& .badge': {
      },
      '& .btn': {
        border: 'none !important',
        boxShadow: 'none !important'
      },
      '& .form-control': {
        border: 'none !important',
        boxShadow: 'none !important'
      },
      '& .table': {
        border: 'none !important'
      },
      '& .table td, .table th': {
        border: 'none !important'
      }
    }
  }
}

export default ItinerairesB2C
